import { useState } from 'react';
import { Loader2, MapPin, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { useAvailableBusinesses } from '@/hooks/review-platforms/useOAuthAuthorizationUrl';
import { useSelectBusinessLocation } from '@/hooks/review-platforms/useSelectBusinessLocation';
import { type AvailableFacebookPage, type AvailableGoogleLocation } from '@/types/review-platform';

interface BusinessLocationPickerProps {
  open: boolean;
  platformSlug: 'google' | 'facebook';
  oauthTokenId: string;
  locationId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Renders after an OAuth callback. Pulls the list of Google business
 * locations / Facebook pages tied to the just-completed OAuth token and
 * lets the contractor pick one, finalizing the connect flow.
 */
export function BusinessLocationPicker({
  open,
  platformSlug,
  oauthTokenId,
  locationId,
  onClose,
  onSuccess,
}: BusinessLocationPickerProps) {
  const { data, isLoading, isError } = useAvailableBusinesses(
    platformSlug,
    open ? oauthTokenId : null,
  );
  const select = useSelectBusinessLocation(locationId);
  const [picked, setPicked] = useState<string | null>(null);

  function pickedItem(): {
    externalBusinessId: string;
    label: string;
    profileUrl?: string;
  } | null {
    if (!picked) return null;
    if (platformSlug === 'google') {
      const item = data?.locations?.find((l) => l.externalBusinessId === picked);
      return item
        ? {
            externalBusinessId: item.externalBusinessId,
            label: item.name,
            profileUrl: item.profileUrl,
          }
        : null;
    }
    const item = data?.pages?.find((p) => p.externalBusinessId === picked);
    return item ? { externalBusinessId: item.externalBusinessId, label: item.name } : null;
  }

  function handleConfirm() {
    const item = pickedItem();
    if (!item) return;
    select.mutate(
      {
        platformSlug,
        oauthTokenId,
        externalBusinessId: item.externalBusinessId,
        externalBusinessLabel: item.label,
        ...(item.profileUrl ? { profileUrl: item.profileUrl } : {}),
        ...(locationId ? { locationId } : {}),
      },
      {
        onSuccess: () => {
          setPicked(null);
          onClose();
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Pick your {platformSlug === 'google' ? 'Google location' : 'Facebook page'}
          </DialogTitle>
          <DialogDescription>
            Select the listing Notifi should sync reviews from and post replies to.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto rounded-md border border-border divide-y divide-border/50">
          {isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : isError ? (
            <div className="p-4 text-center text-sm text-red-700">
              Failed to load businesses. Try reconnecting.
            </div>
          ) : platformSlug === 'google' ? (
            <GoogleList locations={data?.locations ?? []} picked={picked} onPick={setPicked} />
          ) : (
            <FacebookList pages={data?.pages ?? []} picked={picked} onPick={setPicked} />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={select.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!picked || select.isPending}>
            {select.isPending ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Connecting…
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoogleList({
  locations,
  picked,
  onPick,
}: {
  locations: AvailableGoogleLocation[];
  picked: string | null;
  onPick: (id: string) => void;
}) {
  if (locations.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No Google business locations were returned. Make sure your account manages at least one
        verified listing.
      </div>
    );
  }
  return (
    <>
      {locations.map((loc) => (
        <label
          key={loc.externalBusinessId}
          className={`flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-muted/40 ${
            picked === loc.externalBusinessId ? 'bg-primary/5' : ''
          }`}
        >
          <input
            type="radio"
            name="business"
            checked={picked === loc.externalBusinessId}
            onChange={() => onPick(loc.externalBusinessId)}
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate">{loc.name}</p>
              {typeof loc.averageRating === 'number' && (
                <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {loc.averageRating.toFixed(1)}
                </span>
              )}
            </div>
            {loc.address && (
              <p className="truncate text-xs text-muted-foreground">
                <MapPin className="mr-1 inline h-3 w-3" />
                {loc.address}
              </p>
            )}
            {typeof loc.reviewCount === 'number' && (
              <p className="text-[11px] text-muted-foreground">{loc.reviewCount} reviews</p>
            )}
          </div>
        </label>
      ))}
    </>
  );
}

function FacebookList({
  pages,
  picked,
  onPick,
}: {
  pages: AvailableFacebookPage[];
  picked: string | null;
  onPick: (id: string) => void;
}) {
  if (pages.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No Facebook pages were returned. Make sure your account has admin access to at least one
        page.
      </div>
    );
  }
  return (
    <>
      {pages.map((page) => (
        <label
          key={page.externalBusinessId}
          className={`flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-muted/40 ${
            picked === page.externalBusinessId ? 'bg-primary/5' : ''
          }`}
        >
          <input
            type="radio"
            name="business"
            checked={picked === page.externalBusinessId}
            onChange={() => onPick(page.externalBusinessId)}
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{page.name}</p>
            {page.category && <p className="text-xs text-muted-foreground">{page.category}</p>}
            {typeof page.reviewCount === 'number' && (
              <p className="text-[11px] text-muted-foreground">{page.reviewCount} reviews</p>
            )}
          </div>
        </label>
      ))}
    </>
  );
}
